/**
 * Coerce arbitrary parsed JSON into a complete, self-consistent
 * {@link ScenarioModel}, field by field, falling back to a sane default for
 * anything missing or unusable.
 *
 * Three things flow through here: a `localStorage` recovery mirror, an imported
 * document, and a `ScenarioVersion` snapshot written by an older build. None of
 * them can be trusted to be current, complete, or even the right file — so none
 * of them may leave the editor holding an undefined figure.
 *
 * This is deliberately forgiving and is *not* the validation layer: the zod
 * schemas in src/shared/schemas reject a bad request with a message. Normalising
 * is what happens after something has already been accepted, or when there is no
 * one left to show an error to.
 */

import {
  DEFAULT_COMMITMENT_YEARS,
  MAX_COMMITMENT_YEARS,
  MAX_HORIZON_YEARS,
  MAX_PACKAGES,
  MIN_COMMITMENT_YEARS,
  MIN_HORIZON_YEARS,
  PACKAGE_COLOR_SLOTS,
} from "../constants.js";
import { blankPackage, blankYear, DEFAULT_CURRENCY_CODE, draftLineItemId } from "./defaults.js";
import {
  BILLING_MODELS,
  CHURN_MODES,
  LINE_BASES,
  LINE_DIRECTIONS,
  LINE_GROUPS,
  MAINTENANCE_MODES,
  RECURRING_PERIODS,
} from "./types.js";
import type {
  BillingModel,
  ChurnMode,
  LineBasis,
  LineDirection,
  LineGroup,
  LineItemInput,
  MaintenanceMode,
  PackageInput,
  RecurringPeriod,
  ScenarioModel,
  YearInput,
} from "./types.js";

/* ------------------------------------------------------------------ scalars */

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** Numeric strings are accepted: every `<input type="number">` emits them. */
function toNumber(value: unknown, fallback: number): number {
  const parsed = typeof value === "string" ? Number(value.trim()) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  return clamp(Math.round(toNumber(value, fallback)), min, max);
}

/** Money and other non-negative quantities: the direction carries the sign. */
function nonNegative(value: unknown, fallback = 0): number {
  return Math.max(0, toNumber(value, fallback));
}

function toBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
}

function toText(value: unknown, fallback: string, maxLength = 200): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.slice(0, maxLength) : fallback;
}

function oneOf<T extends string>(options: readonly T[], value: unknown, fallback: T): T {
  return typeof value === "string" && (options as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/** ISO-4217 is three letters; anything else is not a currency we can label. */
function toCurrencyCode(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : fallback;
}

/* ----------------------------------------------------------------- packages */

function normalizePackage(raw: unknown, index: number, takenIds: Set<string>): PackageInput {
  const source = asRecord(raw);
  const fallback = blankPackage(index);

  // An id is only useful if it is unique within the model: the collection diff
  // on save keys on it, and so does every mix entry.
  const candidate = typeof source.id === "string" ? source.id.trim() : "";
  let id = candidate.length > 0 && !takenIds.has(candidate) ? candidate : fallback.id;
  for (let suffix = 2; takenIds.has(id); suffix += 1) id = `${fallback.id}-${suffix}`;
  takenIds.add(id);

  const billingModel: BillingModel = oneOf(BILLING_MODELS, source.billingModel, "SUBSCRIPTION");
  const recurringPeriod: RecurringPeriod = oneOf(
    RECURRING_PERIODS,
    source.recurringPeriod,
    "ANNUAL",
  );
  const maintenanceMode: MaintenanceMode = oneOf(MAINTENANCE_MODES, source.maintenanceMode, "NONE");

  return {
    id,
    name: toText(source.name, `Package ${index + 1}`, 80),
    // The array order is the truth after an import; a stored sortOrder that
    // disagrees with it would reorder the page for no visible reason.
    sortOrder: index,
    colorIndex: clampInt(
      source.colorIndex,
      0,
      PACKAGE_COLOR_SLOTS - 1,
      index % PACKAGE_COLOR_SLOTS,
    ),
    billingModel,
    recurringPrice: nonNegative(source.recurringPrice),
    recurringCost: nonNegative(source.recurringCost),
    recurringPeriod,
    oneTimePrice: nonNegative(source.oneTimePrice),
    oneTimeCost: nonNegative(source.oneTimeCost),
    assetCost: nonNegative(source.assetCost),
    maintenanceMode,
    maintenanceRatePct: clamp(toNumber(source.maintenanceRatePct, 0), 0, 100),
    maintenanceFlatAmount: nonNegative(source.maintenanceFlatAmount),
    maintenanceStartYear: clampInt(
      source.maintenanceStartYear,
      1,
      2,
      fallback.maintenanceStartYear,
    ),
    maintenanceAttachPct: clamp(toNumber(source.maintenanceAttachPct, 100), 0, 100),
  };
}

/* ---------------------------------------------------------------- line items */

function normalizeLineItem(
  raw: unknown,
  index: number,
  horizonYears: number,
  packageIds: Set<string>,
  takenIds: Set<string>,
): LineItemInput {
  const source = asRecord(raw);

  const candidate = typeof source.id === "string" ? source.id.trim() : "";
  const seed = draftLineItemId(index);
  let id = candidate.length > 0 && !takenIds.has(candidate) ? candidate : seed;
  for (let suffix = 2; takenIds.has(id); suffix += 1) id = `${seed}-${suffix}`;
  takenIds.add(id);

  const direction: LineDirection = oneOf(LINE_DIRECTIONS, source.direction, "COST");
  const group: LineGroup = oneOf(LINE_GROUPS, source.group, "OTHER");
  const basis: LineBasis = oneOf(LINE_BASES, source.basis, "FIXED_PER_YEAR");

  const startYear = clampInt(source.startYear, 1, horizonYears, 1);
  const rawEnd = source.endYear;
  const endYear =
    rawEnd === null || rawEnd === undefined || rawEnd === ""
      ? null
      : clampInt(rawEnd, startYear, horizonYears, horizonYears);

  return {
    id,
    name: toText(source.name, `Line item ${index + 1}`, 120),
    direction,
    group,
    basis,
    // A percentage basis reads `amount` as a percentage, so it has its own ceiling.
    amount:
      basis === "PCT_OF_REVENUE" || basis === "PCT_OF_COST"
        ? clamp(toNumber(source.amount, 0), 0, 100)
        : nonNegative(source.amount),
    unitsPerClient: nonNegative(source.unitsPerClient, 1),
    attachPct: clamp(toNumber(source.attachPct, 100), 0, 100),
    startYear,
    endYear,
    // An id naming a package this model no longer has would silently count
    // nothing; dropping it makes "all packages" the visible, intended answer.
    packageIds: asArray(source.packageIds).filter(
      (value): value is string => typeof value === "string" && packageIds.has(value),
    ),
    sortOrder: index,
  };
}

/* --------------------------------------------------------------------- years */

function normalizeYear(raw: unknown, packages: PackageInput[]): YearInput {
  const source = asRecord(raw);
  const savedMix = asRecord(source.mix);
  const newClients = Math.max(0, Math.round(toNumber(source.newClients, 0)));

  // Keep the parts within the whole, the way the editor's headroom clamp does:
  // a mix that sums above `newClients` is over-allocation, not an extra sale.
  const mix: Record<string, number> = {};
  let budget = newClients;
  for (const pkg of packages) {
    const wanted = Math.max(0, Math.round(toNumber(savedMix[pkg.id], 0)));
    const taken = Math.min(budget, wanted);
    mix[pkg.id] = taken;
    budget -= taken;
  }

  const churnMode: ChurnMode = oneOf(CHURN_MODES, source.churnMode, "COUNT");
  const note = typeof source.note === "string" ? source.note.trim().slice(0, 500) : "";

  return {
    newClients,
    mix,
    churnMode,
    churnCount: Math.max(0, Math.round(toNumber(source.churnCount, 0))),
    churnRatePct: clamp(toNumber(source.churnRatePct, 0), 0, 100),
    ...(note.length > 0 ? { note } : {}),
  };
}

/* --------------------------------------------------------------------- model */

export interface NormalizeOptions {
  /** Used when the document names no currency — the product's, normally. */
  currencyCode?: string;
}

export function normalizeScenarioModel(
  raw: unknown,
  options: NormalizeOptions = {},
): ScenarioModel {
  const source = asRecord(raw);

  const rawYears = asArray(source.years);
  // The horizon is the number of years the model actually carries unless it
  // says otherwise: a truncated document should not grow empty years back.
  const horizonYears = clampInt(
    source.horizonYears,
    MIN_HORIZON_YEARS,
    MAX_HORIZON_YEARS,
    clamp(rawYears.length, MIN_HORIZON_YEARS, MAX_HORIZON_YEARS),
  );

  const packageIds = new Set<string>();
  const packages = asArray(source.packages)
    .slice(0, MAX_PACKAGES)
    .map((entry, index) => normalizePackage(entry, index, packageIds));
  if (packages.length === 0) {
    const fallback = blankPackage(0);
    packages.push(fallback);
    packageIds.add(fallback.id);
  }

  const lineItemIds = new Set<string>();
  const lineItems = asArray(source.lineItems).map((entry, index) =>
    normalizeLineItem(entry, index, horizonYears, packageIds, lineItemIds),
  );

  // A lengthened horizon gets years nobody signs up in, carrying the same
  // complete mix every other year has.
  const years: YearInput[] = [];
  for (let index = 0; index < horizonYears; index += 1) {
    years.push(
      index < rawYears.length
        ? normalizeYear(rawYears[index], packages)
        : blankYear(packages.map((pkg) => pkg.id)),
    );
  }

  return {
    currencyCode: toCurrencyCode(
      source.currencyCode,
      toCurrencyCode(options.currencyCode, DEFAULT_CURRENCY_CODE),
    ),
    horizonYears,
    commitmentYears: clampInt(
      source.commitmentYears,
      MIN_COMMITMENT_YEARS,
      MAX_COMMITMENT_YEARS,
      DEFAULT_COMMITMENT_YEARS,
    ),
    discountRatePct: clamp(toNumber(source.discountRatePct, 0), 0, 100),
    reuseAssets: toBoolean(source.reuseAssets, true),
    packages,
    lineItems,
    years,
  };
}

/**
 * `normalizeScenarioModel` is forgiving enough that an unrelated JSON file would
 * import as "all defaults" and look like a silent wipe. Require at least one
 * field we recognise before accepting a document as one of ours.
 */
const MODEL_KEYS = ["packages", "years", "lineItems", "horizonYears", "commitmentYears"];

export function looksLikeScenarioModel(value: unknown): boolean {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  return MODEL_KEYS.some((key) => key in (value as Record<string, unknown>));
}
